import { action, computed, observable, reaction } from 'mobx';
// import { showInfoToast } from 'core/components';
import { api } from 'core/util';
import moment from 'moment';
import JourneyCollection from 'app/views/core/journeys/JourneyCollection';
import ChatSessionCollection from 'app/views/core/kaizen/models/ChatSessionCollection';
import { uuidv7 } from 'uuidv7';

// const supportsNotifications =
//   typeof window !== 'undefined' && 'Notification' in window && typeof Notification.requestPermission === 'function';

class JourneysStore {
  // Journeys v1
  collection = new JourneyCollection();

  // Journeys v2 chat sessions
  chatSessionCollection = new ChatSessionCollection();

  // This is used to set the context for the copilot chat
  // Consumers that want to set the context for the copilot chat can call
  // $journeys.setChatContext(context)
  // where context is an object with the following properties:
  // - type: string (e.g. 'device', 'interface', 'cloud vpc')
  // - name: string (e.g. 'agg203.kentik.com')
  // - placeholder: string (e.g. 'Ask about this device')
  // - context: string (e.g. 'Full device details: {...}',
  @observable
  activeChatContext = new Set();

  socket = null;

  @observable
  sendToExplorer = null;

  @observable
  isCopilotOpen = false;

  @observable
  copilotDimensions = {};

  // Tracks the last board-modifying tool result so board views can auto-refresh
  @observable.ref
  lastBoardToolResult = null;

  // Disposer for the session selection reaction — runs at construction time
  _sessionReactionDisposer = reaction(
    () => this.chatSessionCollection.selected,
    (session) => {
      if (session) {
        session.onToolResult = this._handleToolResult;
      }
    }
  );

  @observable
  selectedSessionFeatures = [];

  @computed
  get toolFilterOptions() {
    const contextTypes = new Set(Array.from(this.activeChatContext).map((ctx) => ctx.type));
    const includeToolConfigs = [];

    if (contextTypes.has('notification_template')) {
      includeToolConfigs.push({ name: 'notification_template_helper' });
    }

    return includeToolConfigs.length > 0 ? { includeToolConfigs } : undefined;
  }

  @computed
  get isEnabled() {
    const hasJourneysV2 = this.store?.$auth.hasPermission('kaizen', { overrideForSudo: false });
    const hasAi = !!this.store?.$auth?.canUseAiTools;
    return hasJourneysV2 && hasAi;
  }

  @action
  openCopilot(options = {}) {
    const { isHalfScreen = false } = options;
    if (!this.isEnabled) {
      console.warn('Copilot is not enabled for this user/company');
      return;
    }

    this.isCopilotOpen = true;
    if (isHalfScreen) {
      this.copilotDimensions = {
        width: '50vw',
        height: '90vh'
      };
    }
  }

  @action
  clearCopilotDimensions() {
    this.copilotDimensions = {};
  }

  @action
  closeCopilot() {
    this.minimizeCopilot();
    this.chatSessionCollection.clearSelection();
    this.activeChatContext.clear();
  }

  @action
  minimizeCopilot() {
    this.isCopilotOpen = false;
  }

  @action
  toggleCopilot() {
    this.isCopilotOpen = !this.isCopilotOpen;
  }

  sendMessage(message, options = {}) {
    const { createNewJourney = false } = options;
    const prompt = message.trim();
    const context = this.serializeChatContext();
    const currentToolConfigs = this.toolFilterOptions?.includeToolConfigs || [];

    if (!this.chatSessionCollection?.selected || createNewJourney) {
      const newJourney = this.createJourney();

      if (currentToolConfigs.length > 0) {
        newJourney.set('enabledToolConfigs', currentToolConfigs);
      }

      const agentOptions = currentToolConfigs.length > 0 ? { includeToolConfigs: currentToolConfigs } : undefined;
      const features = this.selectedSessionFeatures.length > 0 ? [...this.selectedSessionFeatures] : undefined;

      newJourney.startChatSessionAndAddPrompt(prompt, context, agentOptions, features);
    } else {
      const selectedSession = this.chatSessionCollection.selected;
      const merged = this.mergeToolConfigs(selectedSession.get('enabledToolConfigs') || [], currentToolConfigs);
      const agentOptions = merged.length > 0 ? { includeToolConfigs: merged } : undefined;

      selectedSession.set('enabledToolConfigs', merged);
      selectedSession.addPrompt(prompt, context, agentOptions);
    }
  }

  mergeToolConfigs(priorConfigs, currentConfigs) {
    const merged = [...priorConfigs];

    for (const config of currentConfigs) {
      if (!merged.some((c) => c.name === config.name)) {
        merged.push(config);
      }
    }

    return merged;
  }

  @action
  updateChatContext = (context) => {
    // Adds or replaces context by type. Use when you want only one context per unique type.
    // Find and remove old context by type
    const oldContext = Array.from(this.activeChatContext).find((ctx) => ctx.type === context.type);
    if (oldContext) {
      this.activeChatContext.delete(oldContext);
    }

    // Add the new context
    this.activeChatContext.add(context);
  };

  @action
  addChatContext = (context) => {
    if (!this.isEnabled) {
      console.warn('Journeys/Copilot is not enabled, cannot add chat context');
      return;
    }
    // Check if context with this name already exists
    const existingContext = Array.from(this.activeChatContext).find((ctx) => ctx.name === context.name);

    if (existingContext) {
      console.info('Context with name already exists, replacing:', context.name);
      this.activeChatContext.delete(existingContext);
    }

    this.activeChatContext.add(context);
  };

  @action
  registerSendToExplorer = (callback) => {
    this.sendToExplorer = callback;
  };

  @action
  unregisterSendToExplorer = () => {
    this.sendToExplorer = null;
  };

  @action
  clearAllChatContext = () => {
    this.activeChatContext.clear();
  };

  @action
  toggleSessionFeature = (featureKey) => {
    const index = this.selectedSessionFeatures.indexOf(featureKey);
    if (index >= 0) {
      this.selectedSessionFeatures.splice(index, 1);
    } else {
      this.selectedSessionFeatures.push(featureKey);
    }
  };

  @action
  clearSessionFeatures = () => {
    this.selectedSessionFeatures.clear();
  };

  @action
  removeChatContext = (context) => {
    const contextToRemove = Array.from(this.activeChatContext).find((ctx) => ctx.name === context.name);
    if (contextToRemove) {
      this.activeChatContext.delete(contextToRemove);
    }
  };

  serializeChatContext() {
    if (this.activeChatContext.size === 0) {
      return '';
    }

    return Array.from(this.activeChatContext)
      .map((context) => {
        // If simple flag is set, just return the context without type/name prefix
        if (context.simple) {
          return context.context;
        }
        return `${context.type}: ${context.name} - ${context.context}`;
      })
      .join('\n');
  }

  // For testing purposes - can be called from browser console
  // e.g. window.$store.$journeys.testCopilot()
  testCopilot() {
    this.toggleCopilot();
  }

  get baseUrl() {
    return '/v4/core/ai-advisor';
  }

  @action
  createJourney = () => {
    // initializing with id here so it can be hashed when adding to collection
    const newJourney = this.chatSessionCollection.forge({ id: uuidv7() });
    this.chatSessionCollection.add(newJourney);
    return newJourney;
  };

  /**
   * Ensures a ChatSessionModel for the given sessionId exists in the collection,
   * forging one if necessary, then selects and subscribes to it and opens the copilot.
   * This is useful when the chat sessions list may not have been fetched yet
   * (e.g. on Runbook settings/history pages).
   */
  @action
  openSessionInCopilot = (sessionId) => {
    let session = this.chatSessionCollection.get(sessionId);

    if (!session) {
      session = this.chatSessionCollection.forge({ id: sessionId }, { select: false });
      this.chatSessionCollection.add(session);
    }

    return session.selectAndSubscribe({ fetch: true }).then(() => {
      this.openCopilot();
    });
  };

  @action
  initialize() {}

  @action
  _handleToolResult = (event) => {
    const toolName = event?.data?.tool_name;
    const toolInput = event?.data?.tool_input || {};
    const isDashboardAction =
      toolInput.action &&
      ['create', 'edit_board', 'add_panels', 'update_panels', 'remove_panels'].includes(toolInput.action);
    if (toolName === 'ude_dashboard' && isDashboardAction) {
      const rawResult = event?.data?.raw_result || {};
      this.lastBoardToolResult = {
        boardId: rawResult.board_id || rawResult.id,
        action: toolInput.action,
        timestamp: Date.now()
      };
    }
  };

  // onJourneyNotification = (data) => {
  //   console.info('Received Journeys Notification', data);

  //   let notification;

  //   const [journey] = this.collection.add({ id: data.id });

  //   const onClick = () => {
  //     const { id } = data;

  //     this.history.push(`/v4/core/journeys/${id}`);
  //     if (notification) {
  //       notification.close();
  //     }
  //   };

  //   journey.fetch().then(() => {
  //     if (Notification.permission === 'granted') {
  //       console.info('Sending browser notification');
  //       notification = new Notification(data.title, {
  //         icon: 'https://images.ctfassets.net/6yom6slo28h2/33eo83sVM9DGq3WCFA5Uh5/70df4c8ad0906763e6a4d174cef5652c/kentik-ai-badge.png',
  //         body: data.description
  //       });

  //       this.store.$basicNotifications.addNotification({
  //         id: data.id,
  //         path: `/v4/core/journeys/${data.id}`,
  //         message: data.description,
  //         title: data.title,
  //         timestamp: new Date()
  //       });

  //       notification.onclick = onClick;
  //     } else {
  //       console.info('Showing info toast');
  //       showInfoToast(data.description, { title: data.title, action: { onClick, text: 'Launch Journey' } });
  //     }
  //   });
  // };

  exportPdf = (sessionId) => {
    const timestamp = moment.utc().format('YYYYMMDD-HH-mm');
    this.store.$exports.fetchExport({
      path: '/api/ui/export/exportPage',
      type: 'pdf',
      fileName: `ai-advisor-${sessionId}-${timestamp}`,
      exportOptions: {
        location: `/v4/export/ai-advisor/${sessionId}`,
        scale: 1,
        pageWidth: 900,
        pageHeight: 1165,
        sleepDuration: 3000
      }
    });
  };

  startInvestigation(data) {
    return api.post('/api/ui/journeys/investigation/start', { data });
  }

  continueInvestigation(data) {
    return api.post('/api/ui/journeys/investigation/continue', { data });
  }
}

export default new JourneysStore();
